/* eslint-disable no-console */
import { SerializedError, miniSerializeError } from '@reduxjs/toolkit';
import { BaseQueryApi } from '@reduxjs/toolkit/query/react';
import {
  MaybePromise,
  QueryReturnValue,
} from '@rtk-query/graphql-request-base-query/dist/GraphqlBaseQueryTypes';
import { RootState } from 'app/store/store';
import { selectBusinessUnitId } from '../../../features/workstationSlice';
import {
  GTINSearchResponse,
  ItemsSearchReturnResponse,
  TermSearchResponse,
} from './itemsSearchTypes';
import { GET_FULL_ITEMS_QUERY } from './masterQuery.gql';
import { GET_FULL_ITEM_BY_GTIN_QUERY } from './masterQueryGTIN.gql';
import { getFullSearchTerm } from './masterQuerySearchTerm.gql';

const ONLY_ASTERISK_REGEX = /\*+/g;
const ONLY_SPACES_REGEX = /\s+/g;

export const itemsSearchQueryFn = async (
  { search }: { search: string },
  api: BaseQueryApi,
  extraOptions: {},
  baseQuery: (arg: {
    document: string;
    variables?: Record<string, string>;
  }) => MaybePromise<
    QueryReturnValue<
      TermSearchResponse | GTINSearchResponse | ItemsSearchReturnResponse,
      unknown,
      {}
    >
  >,
): Promise<ItemsSearchReturnResponse | { error?: SerializedError | unknown }> => {
  const businessUnitId = selectBusinessUnitId(api.getState() as RootState);

  const searchTerm = search
    .replace(ONLY_ASTERISK_REGEX, '')
    .split(ONLY_SPACES_REGEX)
    .join('* AND ');

  const fullSearchTerm = getFullSearchTerm(searchTerm);

  try {
    const gtinResult = await baseQuery({
      document: GET_FULL_ITEM_BY_GTIN_QUERY,
      variables: { gtin: search, businessUnitId },
    });

    if (gtinResult.error) {
      return gtinResult;
    }

    const data = gtinResult?.data;

    if (data && 'itemIdentifier' in data && data.itemIdentifier?.item) {
      const result: ItemsSearchReturnResponse = {
        data: [
          {
            id: data.itemIdentifier.item.id,
            name: data.itemIdentifier.item.name,
            images: data.itemIdentifier.item.images,
            description: data.itemIdentifier.item.description,
            unitOfMeasure: data.itemIdentifier.item.salesUnitOfMeasurement,
            depositAndFeeRuleIds: data.itemIdentifier.item.depositAndFeeRuleIds,
            taxRuleId: data.itemIdentifier.item.priceSpecifications[0]?.taxRuleId,
            taxableGroupId: data.itemIdentifier.item.priceSpecifications[0]?.taxableGroupId,
            price: data.itemIdentifier.item.priceSpecifications[0]?.value,
            currency: data.itemIdentifier.item.priceSpecifications[0]?.currencyId,
            additionalProperties: data.itemIdentifier.item.additionalProperties,
          },
        ],
      };
      return result;
    }

    const itemsResult = await baseQuery({
      document: GET_FULL_ITEMS_QUERY,
      variables: { searchTerm: fullSearchTerm, businessUnitId },
    });

    if (itemsResult.error) {
      return itemsResult;
    }

    const items = itemsResult?.data;
    if (items && 'items' in items) {
      const result: ItemsSearchReturnResponse = {
        data: items.items.nodes.map((item) => ({
          id:
            item.itemIdentifiers.find((identifier) => identifier.type.includes('GTIN'))?.value ||
            item.itemIdentifiers.find((identifier) => identifier.type === 'PLU')?.value ||
            item.id,
          name: item.name,
          images: item.images,
          description: item.description,
          depositAndFeeRuleIds: item.depositAndFeeRuleIds,
          unitOfMeasure: item.salesUnitOfMeasurement,
          taxRuleId: item.priceSpecifications[0]?.taxRuleId,
          taxableGroupId: item.priceSpecifications[0]?.taxableGroupId,
          price: item.priceSpecifications[0]?.value,
          currency: item.priceSpecifications[0]?.currencyId,
          additionalProperties: item.additionalProperties,
        })),
      };
      return result;
    }
    const emptyData: ItemsSearchReturnResponse = { data: [] };
    return emptyData;
  } catch (error) {
    return { error: miniSerializeError(error) };
  }
};
